import EventEmitter from 'events';
import { notify } from 'app/services/errorReporter';

const EVENT_IDLE = 'idle';
const EVENT_NOT_IDLE = 'notidle';

/**
 * RequestMonitor expects to be passed to the `subscribeRequestMonitor` function in
 * `app/utils/request`, which will call its requestStarted() and requestEnded() methods.
 *
 * Its main purpose is to provide the `onRequestIdle` method for detecting when the request activity
 * becomes idle, as a way of determining when the page is done loading for server side rendering.
 */
export default class RequestMonitor extends EventEmitter {
  constructor() {
    super();

    this.pendingRequestIds = new Set();
  }

  numPendingRequests() {
    return this.pendingRequestIds.size;
  }

  requestStarted(requestInfo) {
    if (!(requestInfo && requestInfo.id)) {
      throw new Error('No request id given');
    }

    if (this.pendingRequestIds.has(requestInfo.id)) {
      notify(new Error('RequestMonitor.requestStarted: request already started'), {
        context: {
          requestInfo,
          pendingRequestIds: this.pendingRequestIds,
        },
      });
    }
    this.pendingRequestIds.add(requestInfo.id);

    if (this.numPendingRequests() === 1) {
      this.emit(EVENT_NOT_IDLE);
    }
  }

  requestEnded(requestInfo) {
    if (!(requestInfo && requestInfo.id)) {
      throw new Error('No request id given');
    }

    if (!this.pendingRequestIds.has(requestInfo.id)) {
      /* eslint-disable-next-line no-console */
      console.error('RequestMonitor.requestEnded: request did not start yet', {
        context: {
          requestInfo,
          pendingRequestIds: this.pendingRequestIds,
        },
      });
    } else {
      this.pendingRequestIds.delete(requestInfo.id);
    }

    if (this.numPendingRequests() === 0) {
      this.emit(EVENT_IDLE);
    }
  }

  /**
   * Returns a promise that resolves when all request activity has been idle (not waiting on any
   * requests to respond, and no new requests started) for `minIdle` milliseconds or more.
   */
  onRequestIdle(minIdle, cancelToken) {
    return new Promise((resolve, reject) => {
      let timeoutId = undefined;

      const stopTimer = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
      };

      const restartTimer = () => {
        stopTimer();

        timeoutId = setTimeout(onDone, minIdle);
      };

      const cleanup = () => {
        this.removeListener(EVENT_IDLE, restartTimer);
        this.removeListener(EVENT_NOT_IDLE, stopTimer);

        stopTimer();
      };

      const onDone = () => {
        cleanup();

        resolve();
      };

      this.on(EVENT_IDLE, restartTimer);
      this.on(EVENT_NOT_IDLE, stopTimer);

      if (this.numPendingRequests() === 0) {
        restartTimer();
      }

      if (cancelToken) {
        cancelToken.promise.then(reason => {
          cleanup();

          reject(reason);
        });
      }
    });
  }
}
